import {Injectable} from '@angular/core';
import {GruppeInvitasjonRessursClient, GruppeInvitasjonTilGodkjenningModell, MedlemId} from "../ressurser/generertNg2HttpKlient";
import {Observable} from "rxjs";
import {createStore, filterNil} from "@ngneat/elf";
import {deleteEntities, selectAllEntities, selectEntity, upsertEntities, withEntities} from "@ngneat/elf-entities";
import {FeilhåndteringArgumenter, serverFraLagerOgRequestPakketIMeta, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "./datatjeneste";
import {DataOgMeta, mapTilData} from "./DataOgMeta";
import {HttpResponse} from "@angular/common/http";
import {i18n, Oversettelser} from "../i18n/Oversettelser";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {initDebugger} from "../../støttefunksjoner/debug";
import {InvitasjonOtpSendtModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";

const debug = initDebugger('InvitasjonerDatatjeneste');

/** Lager for {@link GruppeInvitasjonTilGodkjenningModell} */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class InvitasjonerDatatjeneste {

    /** Lager for {@link GruppeInvitasjonTilGodkjenningModell}.
     * Uten forelder, da vi ikke gjør oppslag mot noen logisk forelder */
    private invitasjonerTilGodkjenningLager = createStore(
      {name: 'invitasjonerTilGodkjenning'},
      withEntities<GruppeInvitasjonTilGodkjenningModell>());

    constructor(private gruppeInvitasjonRessurs: GruppeInvitasjonRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser) {
    }


    hentMedMeta(id: MedlemId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<GruppeInvitasjonTilGodkjenningModell>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.gruppeInvitasjonRessurs.hent(id),
            registrerILager: (respons: HttpResponse<GruppeInvitasjonTilGodkjenningModell>) => {
                const invitasjon: GruppeInvitasjonTilGodkjenningModell = respons.body!;
                this.invitasjonerTilGodkjenningLager.update(upsertEntities(invitasjon))
                return invitasjon;
            },
            hentFraLager: () => {
                return this.invitasjonerTilGodkjenningLager.pipe(selectEntity(id), filterNil());
            },
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke hente invitasjon')), 'hentMedMeta')
        });
    }

    hent(id: MedlemId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<GruppeInvitasjonTilGodkjenningModell> {
        return this.hentMedMeta(id, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    invitasjonerTilGodkjenningMedMeta(feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<GruppeInvitasjonTilGodkjenningModell[]>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.gruppeInvitasjonRessurs.finnInvitasjonerTilGodkjenning(),
            registrerILager: (respons: HttpResponse<GruppeInvitasjonTilGodkjenningModell[]>) => {
                const invitasjoner = respons.body!;
                this.invitasjonerTilGodkjenningLager.update(upsertEntities(invitasjoner))
                return invitasjoner;
            },
            hentFraLager: () => {
                return this.invitasjonerTilGodkjenningLager.pipe(selectAllEntities());
            },
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke hente invitasjoner')), 'invitasjonerTilGodkjenning')
        });
    }

    invitasjonerTilGodkjenning(feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<GruppeInvitasjonTilGodkjenningModell[]> {
        return this.invitasjonerTilGodkjenningMedMeta(somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    async sendOtpTilInnloggetBruker(id: MedlemId): Promise<InvitasjonOtpSendtModell> {
        return this.gruppeInvitasjonRessurs.sendOtpTilInnloggetBruker(id).somPromise();
    }

    /** Godtar invitasjon med angitt id.
     * Returnerer {@link GruppeInvitasjonTilGodkjenningModell} med otpValidert == false dersom angitt engangskode ikke er gyldig. */
    async godtaInvitasjonSomEksisterendeBruker(id: MedlemId, engangskode: string): Promise<GruppeInvitasjonTilGodkjenningModell> {
        const oppdatertInvitasjon = await this.gruppeInvitasjonRessurs.godtaInvitasjonSomEksisterendeBruker(id, {engangskode: engangskode}).somPromise();
        if (oppdatertInvitasjon.otpValidert)
          //invitasjon godtatt, skal da ikke lenger vises
            this.invitasjonerTilGodkjenningLager.update(deleteEntities(id));
        else
            this.invitasjonerTilGodkjenningLager.update(upsertEntities(oppdatertInvitasjon));
        return oppdatertInvitasjon;
    }

    async avslåInvitasjonSomEksisterendeBruker(id: MedlemId): Promise<void> {
        await this.gruppeInvitasjonRessurs.avslåInvitasjonSomEksisterendeBruker(id).somPromise();
        this.invitasjonerTilGodkjenningLager.update(deleteEntities(id));
    }


    //hjelpemetoder


    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(feilhåndteringsstrategi: STRATEGI,
                                                                               brukerrettetFeilmelding: string,
                                                                               metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

}
